import React, { useState, useEffect } from 'react';
import { Modal, Input, message, Button, Select, Alert } from 'antd';
import { WhatsAppOutlined } from '@ant-design/icons';
import { makePostCall, getUserDetails, calculateExactAge } from '../utils/helper';
import moment from 'moment';
import { FieldLabels } from '../constants/fieldLabels';

const WhatsAppShareModal = ({
  visible,
  onCancel,
  studyUID,
  pacOrdId,
  studyDescription,
  patientName,
  patientMobile,
  patientGender,
  patientDOB,
  studyDate,
  refDoctorName,
  refDoctorContact,
}) => {
  const [loading, setLoading] = useState(false);
  const [patientNumber, setPatientNumber] = useState(patientMobile || '');
  const [doctorName, setDoctorName] = useState(refDoctorName || '');
  const [doctorContact, setDoctorContact] = useState(refDoctorContact || '');
  const [expiryHours, setExpiryHours] = useState(48);
  const [showSameNumberWarning, setShowSameNumberWarning] = useState(false);

  // Check if patient and doctor numbers are the same
  useEffect(() => {
    if (patientNumber && doctorContact && patientNumber.trim() === doctorContact.trim()) {
      setShowSameNumberWarning(true);
    } else {
      setShowSameNumberWarning(false);
    }
  }, [patientNumber, doctorContact]);

  const handleSendWhatsApp = async () => {
    // Validate mobile numbers
    const mobileNumbers = [];

    if (patientNumber) {
      if (!/^[6-9]\d{9}$/.test(patientNumber)) {
        message.error('Please enter a valid patient mobile number');
        return;
      }
      mobileNumbers.push(patientNumber);
    }

    if (doctorContact) {
      if (!/^[6-9]\d{9}$/.test(doctorContact)) {
        message.error('Please enter a valid doctor contact number');
        return;
      }
      mobileNumbers.push(doctorContact);
    }

    if (mobileNumbers.length === 0) {
      message.error('Please provide at least one mobile number');
      return;
    }

    setLoading(true);

    try {
      // Calculate patient age
      const patientAge = patientDOB ? calculateExactAge(patientDOB) : null;
      const formattedStudyDate = studyDate ? moment(studyDate).format('DD-MM-YYYY') : 'N/A';

      const payload = {
        study_uid: studyUID,
        user_id: getUserDetails()?.username,
        study_description: studyDescription || 'N/A',
        expires_in_hours: expiryHours,
        mobile_numbers: mobileNumbers,
        patient_name: patientName || 'Unknown',
        patient_age: patientAge,
        patient_gender: patientGender || 'Unknown',
        study_date: formattedStudyDate,
        pacs_ord_id: pacOrdId,
      };

      const response = await makePostCall('/generate-study-link', payload);

      if (response.data.success) {
        message.success('Study link sent successfully on WhatsApp!');
        onCancel();
      } else {
        message.error('Failed to send study link');
      }
    } catch (error) {
      console.error('Error sending WhatsApp link:', error);
      message.error('Failed to send study link');
    } finally {
      setLoading(false);
    }
  };

  const expiryOptions = [
    { label: '6 Hours', value: 6 },
    { label: '24 Hours', value: 24 },
    { label: '48 Hours', value: 48 },
    { label: '7 Days', value: 168 },
    { label: 'Never Expire', value: null },
  ];

  return (
    <Modal
      title="Share Study on WhatsApp"
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={500}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
            Study Information:
          </label>
          <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
            {FieldLabels.PATIENT_NAME}: {patientName || 'Unknown'}
            <br />
            Study: {studyDescription || 'N/A'}
            <br />
            Date: {studyDate ? moment(studyDate).format('DD-MM-YYYY') : 'N/A'}
          </p>
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
            Link Expiry Time:
          </label>
          <Select
            value={expiryHours}
            onChange={setExpiryHours}
            options={expiryOptions}
            style={{ width: '100%' }}
            disabled={loading}
          />
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
            {FieldLabels.PATIENT_NAME}
          </label>
          <Input
            value={patientName}
            disabled
            placeholder={FieldLabels.PATIENT_NAME}
          />
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
            Patient Mobile Number:
          </label>
          <Input
            value={patientNumber}
            onChange={e => setPatientNumber(e.target.value)}
            placeholder="Enter patient mobile number"
            maxLength={10}
          />
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
            {FieldLabels.REFERRING_PHYSICIAN}:
          </label>
          <Input
            value={doctorName}
            onChange={e => setDoctorName(e.target.value)}
            placeholder="Enter doctor name"
          />
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
            Doctor Contact Number:
          </label>
          <Input
            value={doctorContact}
            onChange={e => setDoctorContact(e.target.value)}
            placeholder="Enter doctor contact number"
            maxLength={10}
          />
        </div>

        {showSameNumberWarning && (
          <Alert
            message="Warning: Same Contact Number"
            description="The patient mobile number and doctor contact number are the same. The link will only be sent once to this number."
            type="warning"
            showIcon
            closable
          />
        )}

        <Button
          type="primary"
          icon={<WhatsAppOutlined />}
          onClick={handleSendWhatsApp}
          loading={loading}
          disabled={loading}
          style={{
            backgroundColor: '#25D366',
            borderColor: '#25D366',
            color: 'white',
            height: '40px',
            fontSize: '16px',
          }}
        >
          Send Link on WhatsApp
        </Button>

        <div
          style={{ background: '#f0f8ff', padding: '12px', borderRadius: '6px', fontSize: '13px' }}
        >
          <strong>Note:</strong>
          <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
            <li>
              {expiryHours === null
                ? 'Link will never expire'
                : `Link will expire in ${expiryHours} hours`}
            </li>
            <li>Link will be sent to all provided mobile numbers</li>
            <li>At least one mobile number is required</li>
          </ul>
        </div>
      </div>
    </Modal>
  );
};

export default WhatsAppShareModal;
